import { defineStore, acceptHMRUpdate } from 'pinia';
import Planning from '@/models/Planning';
import ProjectSrv from '@/components/Projects/ProjectSrv';
import usePlanningStore from './planning/planning';
import usePlanningLanesStore from './planning/lanes';

function setSubLaneId(subLane) {
  const newId = `${subLane.id}.${window.uuid()}`;
  subLane.o_id = subLane.id;
  subLane.id = newId;
  subLane.hidden = false;
}

function setSubElId(subEl, subplanning) {
  subEl.o_id = subEl.id;
  subEl.id = `${subEl.id}.${window.uuid()}`;
  subEl.project_id = subplanning.id;
}

function setSubElLaneId(subEl, lanesMatchIndex) {
  // lanesMatchIndex : { lane.o_id: lane.id }
  const newLaneId = lanesMatchIndex[subEl.getLaneId()];
  if (newLaneId) subEl.setLaneId(newLaneId);
}

const store = defineStore('subprojects', {
  state: () => ({
    byLaneId: {},
    promises: [],
  }),

  getters: {
    getAll() {
      return Object.keys(this.byLaneId).map(id => this.byLaneId[id]);
    },
    getSubprojectByLaneId() {
      return id => this.byLaneId[id];
    },
    getLaneSubProject() {
      return (laneId) => {
        const planIds = Object.keys(this.byLaneId);
        for (let i = 0; i < planIds.length; i++) {
          const plan = this.byLaneId[planIds[i]];
          const lane = plan.lanes.find(item => item.id == laneId);
          if (lane) return { planning: plan, lane };
        }
        return null;
      };
    },
    getAllElements() {
      const keys = Object.keys(this.byLaneId);
      return keys.reduce((prevEls, key) => prevEls.concat(this.byLaneId[key].elements), []);
    },
  },

  actions: {
    add({ laneId, projectContent, timeline, visibleTimeline }) {
      if (this.byLaneId[laneId]?.id && projectContent.id) { // keep ref
        const subplanning = this.byLaneId[laneId];
        subplanning.lanes.forEach((lane) => {
          lane.dashboard_id = lane.id;
          lane.id = lane.o_id;
        });
        subplanning.elements.forEach((el) => {
          el.dashboard_id = el.id;
          el.id = el.o_id;
          // Warning : el.getLaneId() is lane.dashboard_id and not lane.o_id
          // No impact on update as planning patches compare projectContents, except when lane_id changes
        });

        subplanning.update(projectContent);

        const lanesMatchIndex = {};
        subplanning.lanes.forEach((subLane) => {
          if (subLane.dashboard_id) {
            subLane.id = subLane.dashboard_id;
            delete subLane.dashboard_id;
          } else { // new lane
            setSubLaneId(subLane);
          }
          lanesMatchIndex[subLane.o_id] = subLane.id;
        });
        subplanning.elements.forEach((subEl) => {
          if (subEl.dashboard_id) {
            subEl.id = subEl.dashboard_id;
            delete subEl.dashboard_id;
          } else { // new el
            setSubElId(subEl, subplanning);
          }
          setSubElLaneId(subEl, lanesMatchIndex); // new el or change lane
        });
      } else {
        const subplanning = new Planning(projectContent);

        const lanesMatchIndex = {};
        subplanning.lanes.forEach((subLane) => {
          setSubLaneId(subLane);
          lanesMatchIndex[subLane.o_id] = subLane.id;
        });

        subplanning.elements.forEach((subEl) => {
          setSubElId(subEl, subplanning);
          setSubElLaneId(subEl, lanesMatchIndex);
        });

        if (timeline) subplanning.timeline = timeline;
        if (visibleTimeline) subplanning.visibleTimeline = visibleTimeline;
        this.byLaneId[laneId] = subplanning;
      }
    },

    addSubplanningElement({ projectId, el }) {
      const laneId = Object.keys(this.byLaneId).find(item => this.byLaneId[item].id == projectId);
      const subplanning = this.byLaneId[laneId];
      if (! subplanning) return;
      const subplanningLane = subplanning.lanes.find(lane => lane.o_id == el.getLaneId());
      if (! subplanningLane) return;
      setSubElId(el, subplanning);
      setSubElLaneId(el, { [subplanningLane.o_id]: subplanningLane.id });
      subplanning.elements.push(el);
    },

    delete(id) {
      delete this.byLaneId[id];
    },

    reset() {
      this.byLaneId = {};
      this.promises = [];
    },

    fetchSubproject({ id, lane, viewId }) {
      const planningStore = usePlanningStore();
      const { planning } = planningStore;
      const laneId = lane.id;
      if (! lane) return Promise.reject(new Error('lane not found'));
      let request;
      if (viewId) {
        request = ProjectSrv.getView({ viewId, planningId: id, planningRotoken: lane.project_rotoken });
      } else {
        request = ProjectSrv.get(id, lane.project_rotoken);
      }
      request = request.then((content) => {
        this.add({ laneId, projectContent: content, timeline: planning.timeline, visibleTimeline: planning.visibleTimeline });
        const subplanning = this.byLaneId[laneId];
        // update rotoken and project title
        let changed = false;
        if (subplanning.meta?.read_only_token && subplanning.meta.read_only_token != lane.project_rotoken) {
          lane.project_rotoken = subplanning.meta.read_only_token;
          changed = true;
        }
        if (subplanning.meta?.title && subplanning.meta.title != lane.label) {
          lane.label = subplanning.meta.title;
          changed = true;
        }
        if (changed) planningStore.save();
        return subplanning;
      }).catch((error) => {
        this.add({ laneId, projectContent: { lanes: [{ label: String(error) }] } });
        const subplanning = this.byLaneId[laneId];
        return subplanning;
      });

      this.promises.push(request);
      return request;
    },

    removeByLaneId(id) {
      if (! this.byLaneId[id]) return;
      this.delete(id);
    },

    toggleHiddenSublane({ lane, sublane }) {
      const planningLanesStore = usePlanningLanesStore();
      if (! lane.project_hidden_sublanes) lane.project_hidden_sublanes = [];
      const subprojectLaneOriginalId = parseInt(sublane.id, 10);
      const index = lane.project_hidden_sublanes.indexOf(subprojectLaneOriginalId);
      if (index > -1) {
        lane.project_hidden_sublanes.splice(index, 1);
        const sublaneElements = planningLanesStore.getLaneElements({ planning: sublane.getPlanning(), laneId: sublane.id }) || [];
        setTimeout(() => {
          sublaneElements.forEach((el) => {
            el.update();
            el.updateHeight();
          });

          sublane.contentHeight += 1; // trigger again lane height recalculation now that elements have updated heights
        });
      } else {
        lane.project_hidden_sublanes.push(subprojectLaneOriginalId);
      }
    },

    onChangeTimeline() {
      const { planning } = usePlanningStore();
      const keys = Object.keys(this.byLaneId);
      keys.forEach((key) => {
        this.byLaneId[key].timeline = planning.timeline;
        this.byLaneId[key].visibleTimeline = planning.visibleTimeline;
        this.byLaneId[key].elements.forEach(el => el.update());
      });
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
